import React, { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { PageHeader, EmptyState, Loader } from "@/components/common";
import { TextField, SelectField } from "@/components/fields";
import { money } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Buildings, Plus, PencilSimple, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";

const empty = { name: "", location: "", address: "", phone: "", email: "", currency: "KES", property_manager_id: "", billing_rate: 0, billing_basis: "flat" };

export default function Companies() {
  const [items, setItems] = useState(null);
  const [pms, setPms] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);

  const load = () => {
    api.get("/companies").then((r) => setItems(r.data));
    api.get("/users").then((r) => setPms(r.data.filter((u) => u.role === "property_manager"))).catch(() => {});
  };
  useEffect(() => { load(); }, []);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const openNew = () => { setForm(empty); setEditId(null); setOpen(true); };
  const openEdit = (c) => { setForm({ ...empty, ...c }); setEditId(c.id); setOpen(true); };

  const save = async () => {
    if (!form.name) return toast.error("Company name is required");
    try {
      const payload = { ...form, property_manager_id: form.property_manager_id || null, billing_rate: Number(form.billing_rate) };
      if (editId) await api.put(`/companies/${editId}`, payload);
      else await api.post("/companies", payload);
      toast.success("Saved"); setOpen(false); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this company?")) return;
    await api.delete(`/companies/${id}`); toast.success("Deleted"); load();
  };

  if (!items) return <Loader />;
  const pmName = (id) => pms.find((p) => p.id === id)?.name || "—";

  return (
    <div>
      <PageHeader title="Management Companies" subtitle="Create companies & assign property managers" icon={Buildings}
        actions={<Button onClick={openNew} data-testid="add-company-btn" className="gap-2"><Plus size={16} /> New Company</Button>} />

      {items.length === 0 ? (
        <EmptyState icon={Buildings} title="No companies yet" message="Create a management company and assign a property manager."
          action={<Button onClick={openNew}><Plus size={16} className="mr-2" />New Company</Button>} />
      ) : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Company</TableHead><TableHead>Manager</TableHead><TableHead>Properties</TableHead>
                <TableHead>Billing</TableHead><TableHead></TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {items.map((c) => (
                  <TableRow key={c.id} data-testid={`company-row-${c.id}`}>
                    <TableCell><div className="font-medium">{c.name}</div><div className="text-xs text-muted-foreground">{c.location}</div></TableCell>
                    <TableCell className="text-sm">{pmName(c.property_manager_id)}</TableCell>
                    <TableCell><Badge variant="outline">{c.property_count ?? 0}</Badge></TableCell>
                    <TableCell className="text-sm font-mono">{money(c.billing_rate, c.currency)} <span className="text-xs text-muted-foreground">/{c.billing_basis}</span></TableCell>
                    <TableCell>
                      <div className="flex gap-1 justify-end">
                        <Button size="icon" variant="ghost" onClick={() => openEdit(c)}><PencilSimple size={16} /></Button>
                        <Button size="icon" variant="ghost" onClick={() => del(c.id)}><Trash size={16} className="text-destructive" /></Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{editId ? "Edit Company" : "New Company"}</DialogTitle></DialogHeader>
          <div className="grid sm:grid-cols-2 gap-4">
            <TextField label="Company Name" value={form.name} onChange={(v) => set("name", v)} required testId="company-name" />
            <TextField label="Location" value={form.location} onChange={(v) => set("location", v)} />
            <TextField label="Phone" value={form.phone} onChange={(v) => set("phone", v)} />
            <TextField label="Email" value={form.email} onChange={(v) => set("email", v)} />
            <TextField label="Currency" value={form.currency} onChange={(v) => set("currency", v)} />
            <SelectField label="Property Manager" value={form.property_manager_id} onChange={(v) => set("property_manager_id", v)}
              options={[{ value: "", label: "— Unassigned —" }, ...pms.map((p) => ({ value: p.id, label: p.name }))]} testId="company-pm" />
            <TextField label="Billing Rate" type="number" value={form.billing_rate} onChange={(v) => set("billing_rate", v)} />
            <SelectField label="Billing Basis" value={form.billing_basis} onChange={(v) => set("billing_basis", v)}
              options={[{ value: "flat", label: "Flat Rate" }, { value: "per_property", label: "Per Property" }]} />
            <TextField label="Address" value={form.address} onChange={(v) => set("address", v)} className="sm:col-span-2" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid="save-company-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
